import { BaseService } from './base.service';
import {
  AddProgramTeamPayload,
  BillingInterval,
  BillingPlanKey,
  BillingPlansResponse,
  PlanChangePreview,
  Program,
  ProgramBilling,
  PaidSeasonResponse,
  PaidSetupResponse,
  ProgramCheckoutResponse,
  ProgramInvoiceListItem,
  StartSeasonPayload,
} from '../models/billing.models';

export default class ProgramsService extends BaseService {
  public async getBillingPlans() {
    return this.axios.get<{ data: BillingPlansResponse }>(`${this.backendUrl}/billing/plans`);
  }

  public async getCurrentProgram() {
    return this.axios.get<{ data: Program | null }>(`${this.backendUrl}/programs/current`);
  }

  public async listPrograms() {
    return this.axios.get<{ data: Program[] }>(`${this.backendUrl}/programs`);
  }

  public async startSeason(payload: StartSeasonPayload) {
    return this.axios.post<{ data: { program: Program; teamId: number | null } }>(
      `${this.backendUrl}/programs/start-season`,
      payload
    );
  }

  public async addTeam(programId: number, payload: AddProgramTeamPayload) {
    return this.axios.post<{ data: { program: Program; teamId: number } }>(
      `${this.backendUrl}/programs/${programId}/teams`,
      payload
    );
  }

  public async preparePaidSetup(payload: { planKey: BillingPlanKey; billingInterval: BillingInterval }) {
    return this.axios.post<{ data: PaidSetupResponse }>(`${this.backendUrl}/programs/paid-setup`, payload);
  }

  public async startPaidSeason(payload: StartSeasonPayload & { paymentMethodId?: string; setupIntentId?: string }) {
    return this.axios.post<{ data: PaidSeasonResponse }>(`${this.backendUrl}/programs/start-paid-season`, payload);
  }

  public async finalizePaidSeason(payload: StartSeasonPayload & { subscriptionId: string }) {
    return this.axios.post<{ data: PaidSeasonResponse }>(`${this.backendUrl}/programs/finalize-paid-season`, payload);
  }

  public async abandonPaidCheckout(subscriptionId: string) {
    return this.axios.post(`${this.backendUrl}/programs/abandon-paid-checkout`, { subscriptionId });
  }

  public async checkout(programId: number) {
    return this.axios.post<{ data: ProgramCheckoutResponse }>(`${this.backendUrl}/programs/${programId}/checkout`);
  }

  public async previewChangePlan(
    programId: number,
    payload: { planKey: BillingPlanKey; billingInterval: BillingInterval }
  ) {
    return this.axios.post<{ data: PlanChangePreview }>(
      `${this.backendUrl}/programs/${programId}/change-plan/preview`,
      payload
    );
  }

  public async changePlan(programId: number, payload: { planKey: BillingPlanKey; billingInterval: BillingInterval }) {
    return this.axios.post<{ data: Program }>(`${this.backendUrl}/programs/${programId}/change-plan`, payload);
  }

  public async moveTeamPlan(programId: number, teamId: number, planKey: BillingPlanKey) {
    return this.axios.post<{ data: Program }>(`${this.backendUrl}/programs/${programId}/teams/${teamId}/plan`, {
      planKey,
    });
  }

  public async cancelProgram(programId: number) {
    return this.axios.post<{ data: Program }>(`${this.backendUrl}/programs/${programId}/cancel`);
  }

  public async resumeCancellation(programId: number) {
    return this.axios.post<{ data: Program }>(`${this.backendUrl}/programs/${programId}/resume-cancellation`);
  }

  public async getBilling(programId: number) {
    return this.axios.get<{ data: ProgramBilling | null }>(`${this.backendUrl}/programs/${programId}/billing`);
  }

  public async getInvoices(programId: number) {
    return this.axios.get<{ data: ProgramInvoiceListItem[] }>(`${this.backendUrl}/programs/${programId}/invoices`);
  }

  public async confirmPayment(paymentIntentId: string) {
    return this.axios.post(`${this.backendUrl}/programs/confirm-payment`, {
      payment_intent_id: paymentIntentId,
    });
  }
}
